import React, { useEffect, useState } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../../components/shared/Header';
import { GameEndScreen } from '../../components/game-screens/GameEndScreen';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { HeaderIcons } from '../../components/shared/icons';
import { StrokeReplay, DoodleStroke } from '../_shared/DrawingCanvas';
import { NewPackHostShell } from '../_shared/NewPackHostShell';
import './doodleHeist.css';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/game-screen.css';

interface DoodleState {
  phase: string;
  phaseQuestionId?: string;
  startTime?: number;
  timeLimit?: number;
  galleryIndex?: number;
  galleryTotal?: number;
  banner?: string;
  strokes?: DoodleStroke[];
  options?: Array<{ id: string; letter: string; text: string; isTruth?: boolean }>;
  prompt?: string;
  artistName?: string;
  truthId?: string | null;
  deltas?: Record<string, number>;
  progress?: Array<{ playerId: string; name: string; done: boolean }>;
  standings?: Array<{ playerId: string; playerName: string; score: number; avatar?: string }>;
}

interface Props {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string }>;
    gameState: 'waiting' | 'playing' | 'finished';
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const DoodleHeistHostDisplay: React.FC<Props> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying = false,
}) => {
  const [state, setState] = useState<DoodleState | null>(null);
  const [progress, setProgress] = useState<DoodleState['progress']>([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<
    Array<{ playerId: string; playerName: string; score: number }>
  >([]);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setFinalLeaderboard([]);
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    const onState = (data: DoodleState) => {
      setState(data);
      if (data.progress) setProgress(data.progress);
      if (data.startTime && data.timeLimit) {
        const left = Math.max(0, data.timeLimit - (Date.now() - data.startTime));
        setTimeRemaining(left);
      }
    };
    const onProgress = (data: { progress: DoodleState['progress'] }) => {
      setProgress(data.progress || []);
    };
    const onEnded = (data: {
      leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
    }) => {
      setGameEnded(true);
      setFinalLeaderboard(data.leaderboard || []);
    };

    socket.on('doodle-heist-state', onState);
    socket.on('doodle-heist-progress', onProgress);
    socket.on('game-ended', onEnded);
    socket.emit('get-doodle-heist-state', { roomName: roomState.roomName });

    return () => {
      socket.off('doodle-heist-state', onState);
      socket.off('doodle-heist-progress', onProgress);
      socket.off('game-ended', onEnded);
    };
  }, [socket, roomState.roomName]);

  useEffect(() => {
    if (!state?.startTime || !state.timeLimit || state.phase === 'reveal') return;
    const tick = () => {
      const left = Math.max(0, state.timeLimit! - (Date.now() - state.startTime!));
      setTimeRemaining(left);
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [state?.startTime, state?.timeLimit, state?.phase, state?.phaseQuestionId]);

  if (gameEnded) {
    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameEndData?.gameStatistics ?? null}
        onPlaySameGame={() => {
          socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
          setGameEnded(false);
        }}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  const secs = Math.ceil(timeRemaining / 1000);

  return (
    <NewPackHostShell
      className="doodle-heist-host"
      players={roomState.players}
      questionId={state?.phaseQuestionId}
      shouldUpdateScores={state?.phase === 'reveal' || state?.phase === 'scoreboard'}
      header={
        <Header
          leftButton={
            onBackToGameSelection
              ? {
                  icon: HeaderIcons.back,
                  onClick: onBackToGameSelection,
                  tooltip: 'Back',
                }
              : undefined
          }
          roomCode={roomState.roomName}
          centerContent={
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <span className="game-header-value">Doodle Heist</span>
              {state?.timeLimit && state.phase !== 'reveal' && state.phase !== 'scoreboard' ? (
                <>
                  <div className="game-header-divider header-game-name" />
                  <span className="game-header-value">{secs}s</span>
                </>
              ) : null}
              {hostIsPlaying ? (
                <span className="game-header-label">(also playing)</span>
              ) : null}
            </div>
          }
        />
      }
    >
      <div className="doodle-host-main">
        <div className="doodle-phase-meta">
          {state?.phase === 'draw' && 'Secret studio'}
          {(state?.phase === 'fake' || state?.phase === 'pick' || state?.phase === 'reveal') &&
            `Piece ${(state.galleryIndex ?? 0) + 1} of ${state.galleryTotal ?? '?'}`}
          {state?.phase === 'scoreboard' && 'Mid-game board'}
        </div>
        <p className="doodle-banner">{state?.banner || 'Get ready…'}</p>

        {progress && progress.length > 0 && (
          <div className="doodle-progress">
            {progress.map((p) => (
              <span key={p.playerId} className={`doodle-progress-chip${p.done ? ' done' : ''}`}>
                {p.name}
                {p.done ? ' ✓' : ' …'}
              </span>
            ))}
          </div>
        )}

        {(state?.phase === 'fake' || state?.phase === 'pick' || state?.phase === 'reveal') && (
          <StrokeReplay strokes={state.strokes || []} size={440} />
        )}

        {state?.phase === 'pick' && state.options && (
          <div className="doodle-opts-tv">
            {state.options.map((o) => (
              <div key={o.id} className="doodle-opt-tv">
                <span className="doodle-opt-letter">{o.letter}</span>
                <span>{o.text}</span>
              </div>
            ))}
          </div>
        )}

        {state?.phase === 'reveal' && (
          <>
            <p className="doodle-prompt">
              It was: {state.prompt} — by {state.artistName}
            </p>
            <div className="doodle-opts-tv">
              {(state.options || []).map((o) => (
                <div
                  key={o.id}
                  className={`doodle-opt-tv${o.isTruth || o.id === state.truthId ? ' truth' : ' busted'}`}
                >
                  <span className="doodle-opt-letter">{o.letter}</span>
                  <span>{o.text}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {state?.phase === 'scoreboard' && state.standings && (
          <div className="doodle-standings-list">
            {[...state.standings]
              .sort((a, b) => b.score - a.score)
              .map((row, i) => (
                <div key={row.playerId} className={`doodle-standings-row${i === 0 ? ' top' : ''}`}>
                  <span>
                    #{i + 1} {row.playerName}
                  </span>
                  <span>{row.score}</span>
                </div>
              ))}
          </div>
        )}

        {state?.phase === 'draw' && (
          <p className="doodle-wait">Masterpieces in progress on phones…</p>
        )}
      </div>
    </NewPackHostShell>
  );
};

export default DoodleHeistHostDisplay;
